'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';

interface BrandedLoaderProps {
  /** Optional short message displayed beneath the logo */
  message?: string;
  /** Full-screen overlay mode (default: true) */
  fullScreen?: boolean;
  /** Additional className for the wrapper */
  className?: string;
}

/**
 * BrandedLoader — Naija Explorer branded loading animation.
 *
 * Uses the existing app logo asset with subtle rotating arcs.
 * Supports light/dark mode via CSS variables, prefers-reduced-motion,
 * and aria-live for screen readers.
 *
 * Ring rotation is driven by CSS @keyframes on the compositor thread —
 * immune to main-thread JavaScript blocking on iOS Safari and all
 * modern browsers. prefers-reduced-motion is handled via CSS.
 *
 * When authInitError is true (Supabase unreachable / token refresh hung),
 * replaces the endless spinner with a connection-problem message and a
 * Retry button. loading stays true — no session is destroyed, no redirect
 * to login occurs, protected routes remain protected.
 */
export default function BrandedLoader({
  message = 'Loading...',
  fullScreen = true,
  className = '',
}: BrandedLoaderProps) {
  // Safe to call here: BrandedLoader is always rendered inside AuthProvider's
  // subtree (called from page components, never from AuthProvider itself).
  // No circular dependency, no provider-order problem, no render loop.
  const { authInitError, retryAuthInit } = useAuth();

  const wrapperClass = fullScreen
    ? `fixed inset-0 z-50 flex flex-col items-center justify-center bg-background ${className}`
    : `flex flex-col items-center justify-center py-12 ${className}`;

  return (
    <div className={wrapperClass} role="status" aria-live="polite" aria-label={authInitError ? 'Connection problem' : (message || 'Loading')}>
      {/* Rotating arcs + logo mark */}
      <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>

        {/* Outer slow arc — CSS keyframe clockwise, 3000ms */}
        <svg
          className="absolute inset-0 loader-spin-cw"
          width="96"
          height="96"
          viewBox="0 0 96 96"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="48"
            cy="48"
            r="44"
            stroke="var(--primary)"
            strokeOpacity="0.15"
            strokeWidth="3"
          />
          <circle
            cx="48"
            cy="48"
            r="44"
            stroke="var(--primary)"
            strokeOpacity="0.7"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="60 216"
            strokeDashoffset="0"
          />
        </svg>

        {/* Inner faster arc (accent colour) — CSS keyframe counter-clockwise, 1800ms */}
        <svg
          className="absolute inset-0 loader-spin-ccw"
          width="96"
          height="96"
          viewBox="0 0 96 96"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="48"
            cy="48"
            r="34"
            stroke="var(--accent)"
            strokeOpacity="0.12"
            strokeWidth="2.5"
          />
          <circle
            cx="48"
            cy="48"
            r="34"
            stroke="var(--accent)"
            strokeOpacity="0.6"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="40 174"
            strokeDashoffset="0"
          />
        </svg>

        {/* Logo mark — centred, stationary, no interaction */}
        <div className="relative z-10 w-14 h-14 rounded-2xl bg-primary/8 flex items-center justify-center overflow-hidden">
          <AppImage
            src="/assets/images/76FA5DEB-6D41-4DAD-A034-98609AD757A2-1787860809045.png"
            alt="Naija Explorer"
            width={44}
            height={44}
            className="object-contain"
            priority
          />
        </div>
      </div>

      {authInitError ? (
        /* Connection-problem state — replaces endless spinner message.
           loading stays true in AuthContext: no session destroyed, no redirect,
           protected routes remain protected. Retry re-calls getSession() without
           creating a duplicate onAuthStateChange listener. */
        <div className="mt-5 flex flex-col items-center gap-3 px-6 text-center">
          <p className="text-sm font-medium text-muted-foreground tracking-wide select-none">
            Having trouble connecting…
          </p>
          <p className="text-xs text-muted-foreground/70 select-none max-w-[220px]">
            Check your connection and tap Retry.
          </p>
          <button
            onClick={retryAuthInit}
            className="mt-1 min-h-[44px] min-w-[100px] rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90 active:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-label="Retry connection"
          >
            Retry
          </button>
        </div>
      ) : (
        /* Normal loading message */
        message && (
          <p className="mt-5 text-sm font-medium text-muted-foreground tracking-wide select-none">
            {message}
          </p>
        )
      )}
    </div>
  );
}
